import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, formatearFechaHora, formatearMoneda, type ResumenLicencia, type TipoAcceso } from '@cmms/shared';
import { api, ErrorApi } from '../servicios/api.js';
import { ErroresFormulario } from '../componentes/piezas.js';
import { useSesion } from '../servicios/sesion.js';
import { Aviso, Cargando, Etiqueta, Indicador, Vacio } from '../componentes/piezas.js';

interface CuentaInactiva {
  id: string;
  nombreCompleto: string;
  email: string | null;
  tipoAcceso: TipoAcceso;
  ultimaVisita: string | null;
  estadoInvitacion: string;
  liberaAsiento: boolean;
}

/**
 * Licencia — control de asientos facturados en soles.
 *
 * Muestra lo que se paga y por qué: asientos contratados, cuántos están en uso,
 * y quién los ocupa. La sección de cuentas inactivas existe porque cada asiento
 * facturable ocupado por alguien que nunca ingresó cuesta dinero todos los meses.
 */
export function Licencia(): JSX.Element {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [asientos, setAsientos] = useState<number | null>(null);

  const { data: licencia, isLoading } = useQuery({
    queryKey: ['licencia'],
    queryFn: () => api.get<ResumenLicencia>('/licencia'),
  });

  const { data: inactivas } = useQuery({
    queryKey: ['cuentas-inactivas'],
    queryFn: () => api.get<CuentaInactiva[]>('/licencia/cuentas-inactivas'),
    enabled: puede('licencia:administrar'),
  });

  const ajustar = useMutation({
    mutationFn: (valor: number) => api.post<ResumenLicencia>('/licencia/asientos', { asientosContratados: valor }),
    onSuccess: async () => {
      setAsientos(null);
      await cliente.invalidateQueries({ queryKey: ['licencia'] });
    },
  });

  const desactivar = useMutation({
    mutationFn: (id: string) => api.delete(`/usuarios/${id}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['licencia'] });
      await cliente.invalidateQueries({ queryKey: ['cuentas-inactivas'] });
      await cliente.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });

  if (isLoading) return <Cargando />;
  if (!licencia) return <Vacio titulo="No se pudo cargar la licencia" />;

  const uso = licencia.asientosContratados > 0
    ? (licencia.asientosConsumidos / licencia.asientosContratados) * 100
    : 0;
  const tonoBarra = uso >= 100 ? 'falla' : uso >= 85 ? 'alerta' : '';

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Licencia</h1>
          <p>Cobro por asiento en soles. Solo Administrador y Full User consumen asiento.</p>
        </div>
        <Etiqueta tono={licencia.estado === 'Activa' ? 'verde' : 'ambar'}>Plan {licencia.plan}</Etiqueta>
      </div>

      {ajustar.isError ? <ErroresFormulario error={ajustar.error} /> : null}
      {desactivar.isError ? <ErroresFormulario error={desactivar.error} /> : null}

      {licencia.asientosDisponibles === 0 ? (
        <Aviso tono="atencion">
          No quedan asientos libres. Una nueva cuenta de Administrador o Full User será rechazada hasta
          que amplíe el contrato o desactive una cuenta. Las cuentas de sede seguirán pudiendo crearse:
          el perfil «Únicamente solicitante» es gratuito.
        </Aviso>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador
          titulo="ASIENTOS EN USO"
          valor={`${licencia.asientosConsumidos} / ${licencia.asientosContratados}`}
          nota={`${licencia.asientosDisponibles} disponible(s)`}
        />
        <Indicador
          titulo="PRECIO POR ASIENTO"
          valor={formatearMoneda(licencia.precioAsientoMensualPen)}
          nota="mensual"
        />
        <Indicador titulo="SUBTOTAL MENSUAL" valor={formatearMoneda(licencia.costoMensualPen)} />
        <Indicador
          titulo="TOTAL CON IGV"
          valor={formatearMoneda(licencia.totalMensualPen)}
          nota={`Incluye ${formatearMoneda(licencia.igvPen)} de IGV`}
        />
      </div>

      <section className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cabecera">
          <h2>Consumo</h2>
          {licencia.periodoInicio ? (
            <span style={{ fontSize: 12.5, color: 'var(--texto-tenue)', fontWeight: 600 }}>
              Período desde {formatearFecha(licencia.periodoInicio)}
            </span>
          ) : null}
        </div>
        <div className="panel-cuerpo">
          <div className="barra" style={{ marginBottom: 16 }}>
            <div className={`barra-relleno ${tonoBarra}`} style={{ width: `${Math.min(uso, 100)}%` }} />
          </div>

          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Tipo de acceso</th>
                  <th>Usuarios activos</th>
                  <th>Asiento</th>
                </tr>
              </thead>
              <tbody>
                {licencia.desglose.map((fila) => (
                  <tr key={fila.tipoAcceso}>
                    <td style={{ fontWeight: 600 }}>{fila.tipoAcceso}</td>
                    <td className="numero-registro">{fila.usuarios}</td>
                    <td>
                      {fila.facturable ? (
                        <Etiqueta tono="azul">Consume asiento</Etiqueta>
                      ) : (
                        <Etiqueta tono="verde">Gratuito</Etiqueta>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {puede('licencia:administrar') ? (
            <form
              style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (asientos !== null) ajustar.mutate(asientos);
              }}
            >
              <div className="campo" style={{ marginBottom: 0 }}>
                <label htmlFor="asientos">Asientos contratados</label>
                <input
                  id="asientos"
                  type="number"
                  min={licencia.asientosConsumidos}
                  max={10000}
                  value={asientos ?? licencia.asientosContratados}
                  onChange={(e) => setAsientos(Number(e.target.value))}
                  style={{ width: 130 }}
                />
              </div>
              <button
                type="submit"
                className="boton"
                disabled={ajustar.isPending || asientos === null || asientos === licencia.asientosContratados}
              >
                Actualizar contrato
              </button>
              {asientos !== null && asientos !== licencia.asientosContratados ? (
                <span style={{ fontSize: 13, color: 'var(--texto-medio)' }}>
                  Nuevo total con IGV:{' '}
                  <strong>
                    {formatearMoneda(asientos * Number(licencia.precioAsientoMensualPen) * 1.18)}
                  </strong>
                </span>
              ) : null}
            </form>
          ) : null}
        </div>
      </section>

      {/* §12.6, higiene de cuentas: hay usuarios que nunca se unieron y otros sin
          ingresar desde hace más de un año, y cada uno de los facturables cuesta
          todos los meses. */}
      {puede('licencia:administrar') ? (
        <section className="panel">
          <div className="panel-cabecera">
            <h2>Cuentas inactivas</h2>
            <span className="nav-contador">{inactivas?.length ?? 0}</span>
          </div>

          {inactivas && inactivas.length > 0 ? (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Tipo de acceso</th>
                    <th>Última visita</th>
                    <th>Al desactivar</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {inactivas.map((cuenta) => (
                    <tr key={cuenta.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{cuenta.nombreCompleto}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{cuenta.email ?? '—'}</div>
                      </td>
                      <td>{cuenta.tipoAcceso}</td>
                      <td>
                        {cuenta.ultimaVisita ? formatearFechaHora(cuenta.ultimaVisita) : 'Nunca ingresó'}
                      </td>
                      <td>
                        {cuenta.liberaAsiento ? (
                          <Etiqueta tono="verde">Libera un asiento</Etiqueta>
                        ) : (
                          <span style={{ color: 'var(--texto-tenue)' }}>No afecta la licencia</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="boton secundario pequeno"
                          disabled={desactivar.isPending}
                          onClick={() => desactivar.mutate(cuenta.id)}
                        >
                          Desactivar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Vacio titulo="Sin cuentas inactivas" detalle="Todas las cuentas activas han ingresado recientemente." />
          )}
        </section>
      ) : null}
    </>
  );
}
